/**
 * UI primitives.
 *
 * Deliberately small and local rather than a component-library dependency:
 * everything here is a thin styled wrapper over a native element, so keyboard
 * behaviour, form semantics and screen-reader support come from the platform
 * rather than being reimplemented.
 */

import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { CircleAlert, CircleCheck, Loader2, MoreVertical, X } from 'lucide-react';

export function cn(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(' ');
}

// ── Button ──────────────────────────────────────────────────────────────────

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-800 shadow-sm',
  secondary: 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 active:bg-slate-100',
  ghost: 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
  danger: 'bg-red-600 text-white hover:bg-red-700 active:bg-red-800 shadow-sm',
};

const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-sm gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-11 px-5 text-base gap-2',
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, icon, children, className, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      // A loading button stays disabled so a double-tap cannot submit twice —
      // on a slow connection that is a duplicate sale.
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center rounded-lg font-medium transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-60',
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : icon}
      {children}
    </button>
  );
});

// ── Form fields ─────────────────────────────────────────────────────────────

interface FieldProps {
  label?: string;
  error?: string | undefined;
  hint?: string;
  required?: boolean;
  children: (id: string, describedBy: string | undefined) => ReactNode;
}

/** Wires label, hint and error to the control with the right ARIA attributes. */
export function Field({ label, error, hint, required, children }: FieldProps): ReactNode {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div>
      {label && (
        <label htmlFor={id} className="label-base">
          {label}
          {required && <span className="ml-0.5 text-red-500">*</span>}
        </label>
      )}
      {children(id, describedBy)}
      {hint && !error && (
        <p id={hintId} className="mt-1 text-xs text-slate-500">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="mt-1 text-xs font-medium text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string | undefined;
  hint?: string;
  prefix?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, hint, prefix, className, required, ...rest },
  ref,
) {
  return (
    <Field label={label} error={error} hint={hint} required={required}>
      {(id, describedBy) => (
        <div className="relative">
          {prefix && (
            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-slate-500">
              {prefix}
            </span>
          )}
          <input
            ref={ref}
            id={id}
            aria-describedby={describedBy}
            aria-invalid={error ? true : undefined}
            required={required}
            className={cn(
              'input-base',
              prefix && 'pl-9',
              error && 'border-red-400 focus:border-red-500 focus:ring-red-500/20',
              className,
            )}
            {...rest}
          />
        </div>
      )}
    </Field>
  );
});

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string | undefined;
  hint?: string;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, error, hint, options, placeholder, className, required, ...rest },
  ref,
) {
  return (
    <Field label={label} error={error} hint={hint} required={required}>
      {(id, describedBy) => (
        <select
          ref={ref}
          id={id}
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          required={required}
          className={cn('input-base pr-8', error && 'border-red-400', className)}
          {...rest}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
});

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string | undefined;
  hint?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, error, hint, className, required, ...rest },
  ref,
) {
  return (
    <Field label={label} error={error} hint={hint} required={required}>
      {(id, describedBy) => (
        <textarea
          ref={ref}
          id={id}
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          rows={3}
          className={cn('input-base resize-y', error && 'border-red-400', className)}
          {...rest}
        />
      )}
    </Field>
  );
});

// ── Surfaces ────────────────────────────────────────────────────────────────

export function Card({
  children,
  className,
  padded = true,
}: {
  children: ReactNode;
  className?: string;
  padded?: boolean;
}): ReactNode {
  return <div className={cn('card', padded && 'p-4 sm:p-5', className)}>{children}</div>;
}

export function CardHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
}): ReactNode {
  return (
    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
        {subtitle && <p className="mt-0.5 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

// ── Badge ───────────────────────────────────────────────────────────────────

type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

const BADGE_TONES: Record<BadgeTone, string> = {
  neutral: 'bg-slate-100 text-slate-700 ring-slate-200',
  success: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  warning: 'bg-amber-50 text-amber-700 ring-amber-200',
  danger: 'bg-red-50 text-red-700 ring-red-200',
  info: 'bg-brand-50 text-brand-700 ring-brand-200',
};

export function Badge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: BadgeTone;
}): ReactNode {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
        BADGE_TONES[tone],
      )}
    >
      {children}
    </span>
  );
}

// ── Modal ───────────────────────────────────────────────────────────────────

/**
 * Built on <dialog>, so focus trapping, Escape-to-close, inertness of the
 * background and the top layer are all handled by the browser.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  size = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  size?: 'md' | 'lg';
}): ReactNode {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  if (!open) return null;

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onCancel={onClose}
      // Clicking the backdrop (the dialog element itself) dismisses.
      onClick={(event) => {
        if (event.target === ref.current) onClose();
      }}
      className={cn(
        'w-[calc(100vw-2rem)] rounded-xl border border-slate-200 bg-white p-0 shadow-xl',
        'backdrop:bg-slate-900/40 backdrop:backdrop-blur-sm',
        'max-h-[calc(100dvh-2rem)] overflow-hidden',
        size === 'lg' ? 'sm:max-w-2xl' : 'sm:max-w-lg',
      )}
    >
      <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3.5">
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
        >
          <X className="size-5" />
        </button>
      </div>
      <div className="max-h-[calc(100dvh-10rem)] overflow-y-auto p-5">{children}</div>
    </dialog>
  );
}

// ── Side panel ──────────────────────────────────────────────────────────────

/**
 * A form that slides in from the right, beside the list it changes.
 *
 * The same <dialog> as Modal — focus trapped, Escape closes, the page behind
 * inert — laid against the right edge and the full height of the screen. On a
 * phone it is the whole screen. `footer` stays pinned below the scrolling body
 * so Save is always in reach on a long form.
 */
export function SidePanel({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}): ReactNode {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  if (!open) return null;

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onCancel={onClose}
      onClick={(event) => {
        if (event.target === ref.current) onClose();
      }}
      aria-label={title}
      className={cn(
        'fixed inset-y-0 right-0 left-auto m-0 h-dvh max-h-dvh w-full max-w-full p-0 sm:max-w-xl',
        'border-l border-slate-200 bg-white shadow-2xl',
        'backdrop:bg-slate-900/40 backdrop:backdrop-blur-sm',
      )}
    >
      <div className="flex h-full flex-col">
        <div className="flex items-start justify-between gap-3 border-b border-violet-100 bg-violet-50/60 px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-slate-900">{title}</h2>
            {subtitle ? <div className="mt-0.5 text-sm text-slate-500">{subtitle}</div> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1 text-slate-400 hover:bg-white hover:text-slate-600"
          >
            <X className="size-5" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer ? (
          <div className="border-t border-slate-200 bg-white px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">{footer}</div>
        ) : null}
      </div>
    </dialog>
  );
}

// ── States ──────────────────────────────────────────────────────────────────

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}): ReactNode {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
      {icon && <div className="mb-3 text-slate-300">{icon}</div>}
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      {description && <p className="mt-1 max-w-sm text-sm text-slate-500">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }): ReactNode {
  return (
    <div className="flex items-center justify-center py-12" role="status" aria-live="polite">
      <Loader2 className="size-6 animate-spin text-slate-400" aria-hidden />
      <span className="sr-only">{label}</span>
    </div>
  );
}

export function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}): ReactNode {
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3" role="alert">
      <p className="text-sm text-red-800">{message}</p>
      {onRetry && (
        <Button variant="secondary" size="sm" className="mt-2" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

/** Skeleton rows, so a loading table does not collapse the page layout. */
export function TableSkeleton({ rows = 5, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <div className="divide-y divide-slate-100" aria-hidden>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex gap-4 px-4 py-3.5">
          {Array.from({ length: columns }).map((__, columnIndex) => (
            <div
              key={columnIndex}
              className="h-4 flex-1 animate-pulse rounded bg-slate-100"
              style={{ maxWidth: columnIndex === 0 ? '35%' : undefined }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

// ── Row actions ─────────────────────────────────────────────────────────────

/**
 * A three-dot menu for a table row.
 *
 * `position: fixed` with coordinates taken from the button, rather than an
 * absolutely positioned panel inside the cell: the table scrolls horizontally,
 * and anything positioned within it is clipped by that scroller.
 *
 * The platform's own popover would be the natural fit here and would bring
 * light dismiss, Escape and the top layer with it. It is not used because
 * `[popover]` is hidden by the default stylesheet in environments that style it
 * without implementing `showPopover` — jsdom 30 among them — where the panel
 * would then be permanently invisible rather than merely unenhanced. One
 * implementation that works everywhere beats a better one plus a fallback.
 *
 * So dismissal is explicit: a transparent sheet behind the panel catches the
 * next click anywhere, and Escape closes it.
 */
export function ActionMenu({
  label,
  children,
  align = 'end',
}: {
  label: string;
  children: ReactNode;
  align?: 'start' | 'end';
}): ReactNode {
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);

  /** Below the button, or above it when there is no room. */
  function open(): void {
    const button = trigger.current;
    if (!button) {
      setAt({ top: 0, left: 0 });
      return;
    }

    const rect = button.getBoundingClientRect();
    const height = 280;
    const width = 224;
    const room = window.innerHeight - rect.bottom;

    setAt({
      top: room < height + 12 ? Math.max(8, rect.top - height - 6) : rect.bottom + 6,
      left:
        align === 'end'
          ? Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8))
          : Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
    });
  }

  return (
    <>
      <button
        ref={trigger}
        type="button"
        /*
          `true`, not "menu": the panel is a group of buttons reached with Tab,
          not an ARIA menu with arrow-key navigation. Claiming the menu role
          would promise a screen-reader user keys that do nothing.
        */
        aria-haspopup="true"
        aria-expanded={at !== null}
        aria-label={label}
        onClick={() => (at ? setAt(null) : open())}
        className={cn(
          'inline-flex size-8 items-center justify-center rounded-lg text-slate-500 transition-colors',
          'hover:bg-slate-100 hover:text-slate-700',
          at && 'bg-slate-100 text-slate-700',
        )}
      >
        <MoreVertical className="size-4" aria-hidden />
      </button>

      {at ? (
        <>
          {/*
            The dismiss sheet. `aria-hidden` with no tab stop, so it is a
            pointer target only — keyboard users close with Escape.
          */}
          <div
            aria-hidden
            onClick={() => setAt(null)}
            className="fixed inset-0 z-40 cursor-default"
          />
          <div
            aria-label={label}
            style={{ top: at.top, left: at.left }}
            onKeyDown={(event) => {
              if (event.key === 'Escape') setAt(null);
            }}
            // Any item closes the menu: the click bubbles to here once the
            // item's own handler has run.
            onClick={() => setAt(null)}
            className={cn(
              'fixed z-50 w-56 overflow-hidden rounded-xl border border-slate-200 bg-white p-1 shadow-lg',
              'text-sm',
            )}
          >
            {children}
          </div>
        </>
      ) : null}
    </>
  );
}

/** One line in an {@link ActionMenu}. `tone="danger"` for destructive ones. */
export function MenuItem({
  icon,
  children,
  onSelect,
  tone = 'default',
  disabled,
}: {
  icon?: ReactNode;
  children: ReactNode;
  onSelect: () => void;
  tone?: 'default' | 'danger';
  disabled?: boolean;
}): ReactNode {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-50',
        tone === 'danger'
          ? 'text-red-700 hover:bg-red-50 disabled:hover:bg-transparent'
          : 'text-slate-700 hover:bg-slate-100 disabled:hover:bg-transparent',
      )}
    >
      {icon}
      <span className="min-w-0 flex-1 truncate">{children}</span>
    </button>
  );
}

/** A separator between groups of menu items. */
export function MenuSeparator(): ReactNode {
  return <div className="my-1 h-px bg-slate-100" role="separator" />;
}

// ── Confirmation ────────────────────────────────────────────────────────────

/**
 * Ask before something irreversible, or something that looks it.
 *
 * The question is the title so a screen reader announces it on open, and the
 * confirming button says what it does rather than "OK" — the two places a
 * confirmation usually fails the person reading it quickly.
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel,
  tone = 'danger',
  loading,
  error,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description?: ReactNode;
  confirmLabel: string;
  tone?: 'danger' | 'primary';
  loading?: boolean;
  error?: string | null;
}): ReactNode {
  return (
    <Modal open={open} onClose={onClose} title={title}>
      <div className="space-y-4">
        {description ? <div className="text-sm text-slate-600">{description}</div> : null}

        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="button" variant={tone} onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Toasts ──────────────────────────────────────────────────────────────────

/**
 * Brief confirmation for an action with no dialog left open to report it.
 *
 * Deliberately local: `useToasts` is held by the screen that needs it and
 * rendered by its own `<Toaster/>`, rather than a provider at the app root.
 * Nothing else in the product shows toasts yet, and a global one would be a
 * second notification system competing with the inline alerts every form here
 * already uses.
 */
export interface Toast {
  id: number;
  message: string;
  tone: 'success' | 'error';
}

export function useToasts(): {
  toasts: Toast[];
  show: (message: string, tone?: Toast['tone']) => void;
  dismiss: (id: number) => void;
} {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef<number[]>([]);

  // Clear pending timers on unmount, so a toast raised just before navigating
  // away cannot set state on a component that has gone.
  useEffect(
    () => () => {
      for (const timer of timers.current) window.clearTimeout(timer);
    },
    [],
  );

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback(
    (message: string, tone: Toast['tone'] = 'success') => {
      const id = Date.now() + Math.random();
      setToasts((current) => [...current, { id, message, tone }]);
      timers.current.push(window.setTimeout(() => dismiss(id), tone === 'error' ? 6000 : 4000));
    },
    [dismiss],
  );

  return { toasts, show, dismiss };
}

export function Toaster({
  toasts,
  onDismiss,
}: {
  toasts: Toast[];
  onDismiss: (id: number) => void;
}): ReactNode {
  if (toasts.length === 0) return null;

  return (
    /*
      `role="status"` with a polite live region: the message is read out
      without stealing focus from whatever the person is doing, which is the
      point of a toast. Above the safe-area inset so a phone's home bar does
      not sit on top of it.
    */
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-center gap-2 px-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={cn(
            'pointer-events-auto flex w-full max-w-sm items-start gap-2.5 rounded-xl border px-3.5 py-3 shadow-lg',
            toast.tone === 'error'
              ? 'border-red-200 bg-red-50 text-red-800'
              : 'border-emerald-200 bg-white text-slate-800',
          )}
        >
          {toast.tone === 'error' ? (
            <CircleAlert className="mt-0.5 size-4 shrink-0 text-red-600" aria-hidden />
          ) : (
            <CircleCheck className="mt-0.5 size-4 shrink-0 text-emerald-600" aria-hidden />
          )}
          <p className="min-w-0 flex-1 text-sm">{toast.message}</p>
          <button
            type="button"
            onClick={() => onDismiss(toast.id)}
            aria-label="Dismiss"
            className="rounded p-0.5 text-slate-400 hover:text-slate-600"
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>
      ))}
    </div>
  );
}

// ── Pagination ──────────────────────────────────────────────────────────────

export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}): ReactNode {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (total === 0) return null;

  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-4 py-3">
      <p className="text-sm text-slate-600 tabular">
        <span className="font-medium">{first}</span>–<span className="font-medium">{last}</span> of{' '}
        <span className="font-medium">{total}</span>
      </p>
      <div className="flex gap-2">
        <Button
          variant="secondary"
          size="sm"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          Previous
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
